import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AuthProvider } from "@/lib/AuthContext";
import { AuthDialogProvider } from "@/lib/AuthDialogContext";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "sonner";
import GlobalBackground from "@/components/GlobalBackground";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import ProfileSetup from "@/pages/ProfileSetup";
import StudentDashboard from "@/pages/StudentDashboard";
import Dashboard from "@/pages/Dashboard";
import BrowseNotes from "@/pages/BrowseNotes";
import BrowseLearnings from "@/pages/BrowseLearnings";
import ResetPassword from "@/pages/ResetPassword";
import QuizzesPage from "@/pages/QuizzesPage";
import FlashcardsPage from "@/pages/FlashcardsPage";

const APP_TITLE = "Luminon — Building Brighter Minds";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
        transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<Landing />} />
          <Route path="/onboarding" element={<ProfileSetup />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/student/dashboard" element={<StudentDashboard />} />
          <Route path="/browse/notes" element={<BrowseNotes />} />
          <Route path="/browse/learnings" element={<BrowseLearnings />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/learn/quizzes" element={<QuizzesPage />} />
          <Route path="/learn/flashcards" element={<FlashcardsPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  useEffect(() => {
    // Force the tab title (overrides any 3rd-party script that may set it)
    document.title = APP_TITLE;
    const enforce = () => { if (document.title !== APP_TITLE) document.title = APP_TITLE; };
    const observer = new MutationObserver(enforce);
    const t = document.querySelector("title");
    if (t) observer.observe(t, { childList: true });
    const iv = setInterval(enforce, 1500);
    return () => { observer.disconnect(); clearInterval(iv); };
  }, []);

  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AuthDialogProvider>
              <GlobalBackground />
              <AppRouter />
              <Toaster position="top-right" richColors theme="system" />
            </AuthDialogProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
